import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  X,
  FileSearch,
  MapPin,
  CheckCircle2,
  Clock,
  UserCheck,
  Radio,
  Ban,
  Send,
} from "lucide-react";
import api from "../services/api";
import { getAdminSocket } from "../services/adminSocket";
import {
  describeCancellation,
  formatTripDateTime,
  shortBookingId,
} from "../utils/tripLog";

// Lagos wall-clock time like the rest of the admin, e.g. "06 Aug 2026, 2:30pm".
const fmt = (d) => (d ? formatTripDateTime(d) : "—");

// Relative time between two ISO timestamps, e.g. "8s", "2m 3s".
const rel = (from, to) => {
  if (!from || !to) return null;
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const EVENT_ICON = {
  requested: Send,
  dispatched: Radio,
  accepted: UserCheck,
  assigned: UserCheck,
  completed_by_driver: CheckCircle2,
  completed: CheckCircle2,
  cancelled: Ban,
};

const STATUS_PILL = {
  requested: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  accepted: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  in_progress: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
  awaiting_payment: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

// Why riders near the pickup were not offered the ride (booking.meta.dispatch).
const DISPATCH_REASONS = [
  ["rejectedInactive", (n) => `${n} inactive`],
  ["rejectedStaleLocation", (n) => `${n} with an old location`],
  ["rejectedOutsideGeofence", (n) => `${n} outside Unilorin`],
  ["rejectedRideUnavailable", (n) => `${n} unavailable`],
  ["rejectedNoSeats", (n) => `${n} with no free seats`],
  ["stalePinTreatedFree", (n) => `${n} stuck on an empty trip (offered anyway)`],
];

const describeDispatch = (dispatch) => {
  if (!dispatch || typeof dispatch.nearbyConsidered !== "number") return null;
  const reasons = DISPATCH_REASONS.filter(([key]) => dispatch[key] > 0).map(([key, f]) => f(dispatch[key]));
  // Since Oct 2026 dispatch ignores distance (radiusMeters 0): the count is online riders, not nearby ones.
  const pool = dispatch.radiusMeters === 0 ? "online" : "nearby";
  const head = `${dispatch.nearbyConsidered} ${pool}, ${dispatch.qualified ?? 0} qualified`;
  return reasons.length ? `${head}: ${reasons.join(", ")}` : head;
};

const OFFER_STATUS = {
  notified: { label: "Waiting", cls: "text-amber-600 dark:text-amber-400" },
  accepted: { label: "Accepted", cls: "text-emerald-600 dark:text-emerald-400" },
  ignored: { label: "Ignored", cls: "text-gray-400" },
  expired: { label: "Expired", cls: "text-gray-400" },
  cancelled: { label: "Ride cancelled", cls: "text-rose-600 dark:text-rose-400" },
};

/**
 * Live monitoring timeline for a single booking. Fetches the full timeline and
 * re-fetches whenever the backend nudges (`admin:ride_update`) for this booking.
 */
function RideTimelineModal({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  // The booking itself carries meta.dispatch and assignedByAdminId.
  const [booking, setBooking] = useState(null);
  const [bookingError, setBookingError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [timelineRes, bookingRes] = await Promise.allSettled([
      api.get(`/v1/admin/rides/${bookingId}/timeline`),
      api.get(`/v1/admin/bookings/${bookingId}`),
    ]);
    // A failed refresh keeps the last-known timeline; a failed first load shows an error.
    if (timelineRes.status === "fulfilled") {
      setData(timelineRes.value.data);
      setLoadError(false);
    } else {
      setLoadError(true);
    }
    if (bookingRes.status === "fulfilled") {
      setBooking(bookingRes.value.data?.data || bookingRes.value.data);
      setBookingError(false);
    } else {
      setBookingError(true);
    }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => {
    load();
    const socket = getAdminSocket();
    if (!socket) return;
    const onUpdate = (p) => {
      if (p?.bookingId === bookingId) load();
    };
    socket.on("admin:ride_update", onUpdate);
    // Light poll as a safety net while the modal is open.
    const id = setInterval(load, 10000);
    return () => {
      socket.off("admin:ride_update", onUpdate);
      clearInterval(id);
    };
  }, [bookingId, load]);

  const d = data || {};
  const offers = d.dispatchedTo || [];

  // An admin assign adds an offer for the assigned rider at accept time. That
  // offer is the assignment, not a dispatch, so it is not counted as "sent".
  const assignedByAdmin = Boolean(booking?.assignedByAdminId);
  const acceptedAt = d.acceptedBy?.acceptedAt ? new Date(d.acceptedBy.acceptedAt).getTime() : null;
  const assignedOffer = assignedByAdmin
    ? offers.find(
        (o) =>
          o.driverId === d.acceptedBy?.id &&
          (acceptedAt === null || !o.notifiedAt || new Date(o.notifiedAt).getTime() >= acceptedAt - 2000),
      )
    : null;
  const dispatched = offers.filter((o) => o !== assignedOffer);

  const cancellation = describeCancellation(booking);
  const events = (d.events || []).flatMap((ev) => {
    if (ev.type === "cancelled" && cancellation) {
      return [{ ...ev, detail: cancellation.text }];
    }
    if (ev.type === "dispatched") {
      if (dispatched.length === 0) return [];
      return [{ ...ev, detail: `Sent to ${dispatched.length} rider${dispatched.length === 1 ? "" : "s"}` }];
    }
    if (ev.type === "accepted" && assignedByAdmin) {
      return [{ ...ev, type: "assigned", detail: `Assigned by admin to ${d.acceptedBy?.name || "a rider"}` }];
    }
    return [ev];
  });

  const dispatchText = describeDispatch(booking?.meta?.dispatch);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Ride timeline
              </h2>
              {d.status ? (
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[d.status] || "bg-gray-100 text-gray-600"}`}
                >
                  {String(d.status).replace(/_/g, " ")}
                </span>
              ) : null}
              {d.isCharter ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                  charter
                </span>
              ) : null}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {d.pickupLocationName} → {d.dropoffLocationName}
            </p>
            <p className="mt-1 flex items-center gap-3 text-xs text-gray-400">
              <span className="font-mono">{shortBookingId(bookingId)}</span>
              <Link
                to={`/trip-inspector/${bookingId}`}
                className="inline-flex items-center gap-1 text-gray-500 hover:text-[#FF8C00]"
              >
                <FileSearch className="w-3.5 h-3.5" />
                Inspect
              </Link>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && !data ? (
          <div className="p-10 text-center text-gray-400">Loading timeline…</div>
        ) : loadError && !data ? (
          <div className="p-10 text-center text-sm text-red-600">
            {"Couldn't load the timeline. "}
            <button type="button" onClick={load} className="underline font-medium">
              Retry
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Passenger + accepted-by */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3">
                <p className="text-xs text-gray-400 uppercase tracking-wide">
                  Customer
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {d.passenger?.name || "—"}
                </p>
                {d.passenger?.phone ? (
                  <p className="text-xs text-gray-500">{d.passenger.phone}</p>
                ) : null}
              </div>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3">
                <p className="text-xs text-gray-400 uppercase tracking-wide">
                  Accepted by
                </p>
                {d.acceptedBy ? (
                  <>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {d.acceptedBy.name}
                    </p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      in {rel(d.createdAt, d.acceptedBy.acceptedAt) || "—"}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Not yet accepted</p>
                )}
              </div>
            </div>

            {loadError ? (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                {"Couldn't refresh. Showing the last loaded timeline."}
              </p>
            ) : null}

            {/* Who cancelled and why */}
            {d.status === "cancelled" ? (
              <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 p-3">
                <p className="text-xs text-gray-400 uppercase tracking-wide">
                  Cancellation
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {cancellation?.text ||
                    (bookingError
                      ? "Couldn't load why this ride was cancelled."
                      : "Loading…")}
                </p>
                {cancellation?.at ? (
                  <p className="text-xs text-gray-500">{fmt(cancellation.at)}</p>
                ) : null}
              </div>
            ) : null}

            {/* Why the request reached the riders it reached (or nobody) */}
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide">
                Dispatch
              </p>
              <p className="text-sm text-gray-900 dark:text-white">
                {dispatchText ||
                  (bookingError
                    ? "Couldn't load the dispatch reason."
                    : "Dispatch reason not recorded for this ride.")}
              </p>
            </div>

            {/* Counts over the riders who were actually sent the request */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {dispatched.length}
                </p>
                <p className="text-xs text-gray-500">Riders notified</p>
              </div>
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {dispatched.filter((o) => o.wasInUnilorin === true).length}
                </p>
                <p className="text-xs text-gray-500">Of those notified: in Unilorin</p>
              </div>
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {dispatched.filter((o) => o.wasAvailable === true).length}
                </p>
                <p className="text-xs text-gray-500">Of those notified: online</p>
              </div>
            </div>

            {/* Event log */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Events
              </h3>
              <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-4">
                {events.map((ev, i) => {
                  const Icon = EVENT_ICON[ev.type] || Clock;
                  return (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-[#FF8C00] ring-4 ring-white dark:ring-gray-900">
                        <Icon className="w-2.5 h-2.5 text-white" />
                      </span>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-gray-800 dark:text-gray-200">
                          {ev.detail}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400">{fmt(ev.at)}</p>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Dispatched riders */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Dispatched to {dispatched.length} rider
                {dispatched.length === 1 ? "" : "s"}
                {assignedOffer ? " + 1 assigned by admin" : ""}
              </h3>
              <div className="space-y-2">
                {offers.map((r) => (
                  <div
                    key={r.driverId}
                    className={`flex items-center justify-between rounded-xl border p-3 ${
                      r.status === "accepted"
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                        : "border-gray-100 dark:border-gray-800"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {r.driverName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {r.wasInUnilorin === true ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
                            Unilorin
                          </span>
                        ) : r.wasInUnilorin === false ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-800">
                            outside
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                            location n/a
                          </span>
                        )}
                        {r.wasAvailable ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                            online
                          </span>
                        ) : null}
                        {r === assignedOffer ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                            assigned by admin
                          </span>
                        ) : null}
                        {r.nudgeCount ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                            nudged {r.nudgeCount}×
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-xs font-semibold ${
                          OFFER_STATUS[r.status]?.cls || "text-gray-500"
                        }`}
                      >
                        {OFFER_STATUS[r.status]?.label || r.status}
                      </p>
                      <p className="text-[11px] text-gray-400">{fmt(r.notifiedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RideTimelineModal;
